import React, { useState, useEffect } from 'react';
import { fetchSettings, fetchResetToken, resetApp } from '../../api';

const APP_VERSION = require('../../../package.json').version;

const IS_DEV = process.env.NODE_ENV !== 'production';

function DeveloperSection({ settings, setSettings, setMessage, activeNote, onReset }) {
  // ══════ 1. ALL useState ══════
  const [wartungsmodus, setWartungsmodus] = useState(() => {
    const stored = localStorage.getItem('wartungsmodus');
    if (stored !== null) return stored === 'true';
    return IS_DEV;
  });
  const [devTab, setDevTab] = useState('timing');
  const [startupTiming, setStartupTiming] = useState(null);
  const [perfLog, setPerfLog] = useState([]);
  const [debugCopied, setDebugCopied] = useState(false);
  const [resetStep, setResetStep] = useState(0);
  const [resetInput, setResetInput] = useState('');
  const [resetting, setResetting] = useState(false);

  // ══════ 4. ALL useEffect ══════
  useEffect(() => {
    if (wartungsmodus) {
      if (window.electronAPI?.getStartupTiming) {
        window.electronAPI.getStartupTiming().then(setStartupTiming).catch(() => {});
      }
      if (window.__perfLog) {
        setPerfLog([...window.__perfLog]);
      }
    }
  }, [wartungsmodus]);

  const paths = settings._paths || {};

  const handleCopyDebug = () => {
    const payload = {
      app_version: APP_VERSION,
      timestamp: new Date().toISOString(),
      active_note: activeNote
        ? { id: activeNote.id, title: activeNote.title, content: activeNote.content }
        : null,
      paths,
      readwise_connected: !!settings.readwise?.has_api_key,
      wartungsmodus,
      startup_timing: startupTiming || null,
      perf_log: wartungsmodus ? (window.__perfLog || []) : [],
      recent_errors: window.__errorLog || [],
    };
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2)).then(() => {
      setDebugCopied(true);
      setTimeout(() => setDebugCopied(false), 2500);
    });
  };

  const handleReset = async () => {
    setResetting(true);
    setMessage(null);
    try {
      const { token } = await fetchResetToken();
      const result = await resetApp(token);
      setMessage({ type: 'success', text: result.message });
      setResetStep(0);
      setResetInput('');
      const updated = await fetchSettings();
      setSettings(updated);
      onReset?.();
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler beim Zurücksetzen: ' + err.message });
    }
    setResetting(false);
  };

  const handleOpenDevTools = () => {
    if (window.electronAPI?.openDevTools) {
      window.electronAPI.openDevTools();
    }
  };

  const errorLog = window.__errorLog || [];

  return (
    <>
      {/* Wartungsmodus Toggle */}
      <section className="settings-section">
        <h3 className="settings-section-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
          </svg>
          Wartungsmodus
        </h3>
        <p className="settings-desc">
          Zeichnet Startup-Timing und API-Performance auf. Zeigt erweiterte Diagnose-Tools.
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
          <input
            type="checkbox"
            checked={wartungsmodus}
            onChange={(e) => {
              const v = e.target.checked;
              setWartungsmodus(v);
              localStorage.setItem('wartungsmodus', v ? 'true' : 'false');
              window.__perfEnabled = v;
              if (!v) { window.__perfLog = []; setPerfLog([]); }
            }}
            style={{ width: 16, height: 16, accentColor: 'var(--color-primary)' }}
          />
          Wartungsmodus aktivieren
        </label>
      </section>

      {/* Dev Console (nur wenn Wartungsmodus aktiv) */}
      {wartungsmodus && (
        <section className="settings-section settings-section-divider">
          {/* DevTools-Button */}
          {window.electronAPI && (
            <button
              className="settings-btn-secondary"
              style={{ marginBottom: 'var(--space-4)' }}
              onClick={handleOpenDevTools}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
              </svg>
              DevTools öffnen
            </button>
          )}

          {/* Mini-Tabs */}
          <div className="settings-dev-tabs">
            {[
              { id: 'timing', label: 'Timing' },
              { id: 'performance', label: 'Performance' },
              { id: 'errors', label: 'Fehler' },
            ].map(tab => (
              <button
                key={tab.id}
                className={`settings-dev-tab${devTab === tab.id ? ' active' : ''}`}
                onClick={() => setDevTab(tab.id)}
              >
                {tab.label}
                {tab.id === 'errors' && errorLog.length > 0 && (
                  <span className="settings-dev-tab-badge">{errorLog.length}</span>
                )}
                {tab.id === 'performance' && perfLog.length > 0 && (
                  <span className="settings-dev-tab-badge">{perfLog.length}</span>
                )}
              </button>
            ))}
          </div>

          {/* Tab: Timing */}
          {devTab === 'timing' && (
            <div className="settings-dev-panel">
              {startupTiming && startupTiming.steps && startupTiming.steps.length > 0 ? (
                <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12, lineHeight: 1.8, color: 'var(--text-secondary)' }}>
                  {startupTiming.steps.map((step, i) => {
                    const prev = i > 0 ? startupTiming.steps[i - 1].ms : 0;
                    const delta = step.ms - prev;
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                        <span>{step.label}</span>
                        <span style={{ color: delta > 2000 ? 'var(--color-danger, #c00)' : delta > 500 ? 'var(--color-warning, #b80)' : 'var(--text-muted)' }}>
                          +{delta}ms <span style={{ color: 'var(--text-muted)' }}>({step.ms}ms)</span>
                        </span>
                      </div>
                    );
                  })}
                  <div style={{ borderTop: '1px solid var(--border-color, #ddd)', marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                    <span>Gesamt</span>
                    <span>{startupTiming.steps[startupTiming.steps.length - 1].ms}ms</span>
                  </div>
                </div>
              ) : (
                <p className="settings-desc" style={{ margin: 0 }}>
                  Startup-Timing nicht verfügbar. Starte die App im Electron-Modus neu.
                </p>
              )}
            </div>
          )}

          {/* Tab: Performance */}
          {devTab === 'performance' && (
            <div className="settings-dev-panel">
              {perfLog.length > 0 ? (
                <>
                  <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 11, lineHeight: 1.7, color: 'var(--text-secondary)', maxHeight: 240, overflowY: 'auto' }}>
                    {[...perfLog].reverse().map((entry, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, opacity: entry.error ? 0.6 : 1 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {entry.label}{entry.error ? ' ✕' : ''}
                        </span>
                        <span style={{
                          flexShrink: 0,
                          color: entry.ms > 500 ? 'var(--color-danger, #c00)' : entry.ms > 200 ? 'var(--color-warning, #b80)' : 'var(--text-muted)',
                          fontWeight: entry.ms > 500 ? 600 : 400
                        }}>
                          {entry.ms}ms
                        </span>
                      </div>
                    ))}
                  </div>
                  <button
                    className="settings-btn-secondary"
                    style={{ marginTop: 8, fontSize: 12 }}
                    onClick={() => { window.__perfLog = []; setPerfLog([]); }}
                  >
                    Log leeren
                  </button>
                </>
              ) : (
                <p className="settings-desc" style={{ margin: 0 }}>
                  Noch keine API-Aufrufe aufgezeichnet.
                </p>
              )}
            </div>
          )}

          {/* Tab: Fehler */}
          {devTab === 'errors' && (
            <div className="settings-dev-panel">
              {errorLog.length > 0 ? (
                <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 11, lineHeight: 1.7, color: 'var(--color-danger, #c00)', maxHeight: 240, overflowY: 'auto' }}>
                  {errorLog.map((err, i) => (
                    <div key={i} style={{ marginBottom: 6, paddingBottom: 6, borderBottom: '1px solid var(--color-divider)' }}>
                      <div style={{ fontWeight: 600 }}>{err.message || String(err)}</div>
                      {err.source && <div style={{ color: 'var(--text-muted)', fontSize: 10 }}>{err.source}:{err.line}</div>}
                      {err.timestamp && <div style={{ color: 'var(--text-muted)', fontSize: 10 }}>{new Date(err.timestamp).toLocaleTimeString()}</div>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="settings-desc" style={{ margin: 0, color: 'var(--color-success)' }}>
                  Keine Fehler seit dem letzten Start.
                </p>
              )}

              {/* Debug-Info kopieren */}
              <button
                className={`settings-btn-secondary${debugCopied ? ' settings-btn-copied' : ''}`}
                style={{ marginTop: 12 }}
                onClick={handleCopyDebug}
              >
                {debugCopied ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Kopiert!
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    Debug-Info kopieren
                  </>
                )}
              </button>
            </div>
          )}
        </section>
      )}

      {/* Danger Zone: App zurücksetzen */}
      <section className="settings-section settings-section-danger">
        <h3 className="settings-section-title settings-section-title-danger">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          App zurücksetzen
        </h3>
        <p className="settings-desc">
          Löscht <strong>alle Zettel</strong>, alle importierten <strong>Highlights</strong> und setzt den Readwise-Sync zurück. Dein API-Key und deine Einstellungen bleiben erhalten. Nach dem Reset werden alle Highlights beim nächsten Sync neu importiert.
        </p>

        {resetStep === 0 && (
          <button className="settings-btn-danger" onClick={() => setResetStep(1)}>
            App zurücksetzen…
          </button>
        )}

        {resetStep === 1 && (
          <div className="settings-reset-confirm">
            <p className="settings-reset-warning">
              Dieser Schritt kann <strong>nicht rückgängig</strong> gemacht werden. Alle Zettel und Highlights werden permanent gelöscht.
            </p>
            <div className="settings-reset-btn-row">
              <button className="settings-btn-secondary" onClick={() => setResetStep(0)}>
                Abbrechen
              </button>
              <button className="settings-btn-danger" onClick={() => setResetStep(2)}>
                Ja, ich möchte zurücksetzen
              </button>
            </div>
          </div>
        )}

        {resetStep === 2 && (
          <div className="settings-reset-confirm">
            <p className="settings-reset-warning">
              Letzte Bestätigung: Tippe <strong>ZURÜCKSETZEN</strong> um fortzufahren.
            </p>
            <div className="settings-input-row">
              <input
                type="text"
                className="settings-input settings-input-danger"
                value={resetInput}
                onChange={e => setResetInput(e.target.value)}
                placeholder="ZURÜCKSETZEN"
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Escape') { setResetStep(0); setResetInput(''); }
                  if (e.key === 'Enter' && resetInput === 'ZURÜCKSETZEN') handleReset();
                }}
              />
              <button className="settings-btn-secondary" onClick={() => { setResetStep(0); setResetInput(''); }}>
                Abbrechen
              </button>
              <button
                className="settings-btn-danger"
                onClick={handleReset}
                disabled={resetting || resetInput !== 'ZURÜCKSETZEN'}
              >
                {resetting ? 'Wird zurückgesetzt…' : 'Jetzt zurücksetzen'}
              </button>
            </div>
          </div>
        )}
      </section>
    </>
  );
}

export default DeveloperSection;
